import { FormHelperText, makeStyles, styled, withStyles } from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { Linked, useLinked } from '@type-r/react';
import clsx from 'clsx';
import * as React from 'react';
import { ComponentProps, useContext } from 'react';
import { useQaId } from './qa-ids';
import { colors, div } from '../common';
import { Flex } from '../flex';
import { BaseCheckbox } from './checkbox';
import { Input, ReadOnly } from './inputs';
import { BaseSelect } from './select';
import * as LqxLink from '../link';
import { Link } from '../link';

export const FormInput = ( { label, trimOnBlur, 'qa-id' : lqxId, ...props } :
    ComponentProps<typeof Input> & 
    (
        { label : string, trimOnBlur?: boolean, 'qa-id'? : string } |
        { label : React.ReactNode, trimOnBlur?: boolean, 'qa-id' : string }
    )
) => {
    const classes = useTextFieldStyles( props ),
        ro = useContext( ReadOnly ) || props.readOnly || !props.$value || props.disabled,
        error = ro ? '' : props.$value.error;

    const onBlurCopy = props.onBlur;
    if (trimOnBlur) {
      props.onBlur = (e) => {
        if (props.$value && (typeof props.$value.value === 'string')) {
          const trimmedInput = (props.$value.value as String).trim();
          ( props.$value as Linked<string> ).set(trimmedInput as any);
        }
        if (onBlurCopy) {
          onBlurCopy(e);
        }
      }
    }

    const nextId = useQaId( lqxId || label );
    return (
        <label className={ classes.root }>
            <span className={ classes.label }>
                { label }
                { (props.required === undefined && error === 'Required') || ( props.required && !ro ) ?
                    <span className={ classes.required }>*</span>
                : null }
            </span>

            <div className={ classes.formControlInputWithError }>
                <Input {...props} data-qa-id={nextId}/>
                <FormHelperText>{ error !== 'Required' ? error : '' }</FormHelperText>
            </div>
        </label>
    )
}

export const FormDisputeInput = ( { label, value, $value, ...props } : ComponentProps<typeof Input> & { label? : string }) => {
    const classes = useTextFieldStyles(),
        ro = useContext( ReadOnly ) || props.readOnly || !$value,
        error = ro ? '' : $value.error,
        $initialValue = useLinked($value.value),
        // not sure if we still have to cross out the value
        valueChanged = $value.value !== $initialValue.value;

    return (
        <label className={ classes.root }>
            <span className={ clsx(classes.label, classes.disputeLabel) }>
                { label }
                { error === 'Required' ?
                    <span className={ classes.required }>*</span>
                : null }
            </span>

            <div className={ classes.disputeValueContainer }>
                <Input
                    classes={{
                        input: valueChanged ?
                                clsx(classes.disputeValue, classes.disputeValueChanged)
                            :
                                classes.disputeValue
                    }}
                    {...props}
                    readOnly={ true }
                    value={value}
                    $value={undefined}
                />
            </div>

            <div className={ classes.formControlInputWithError }>
                <Input {...props} value={undefined} $value={$value} />
                <FormHelperText>{ error !== 'Required' ? error : '' }</FormHelperText>
            </div>
        </label>
    );
};

export const FormLink = ({label , href = undefined, linkLabel, onClick = undefined}) => {
    const classes = useTextFieldStyles();

    return (
        <label className={ classes.root }>
            <span className={ classes.label }>
                { label }                
            </span>

            <div className={ classes.formControlInputWithError }>
                <Link className={ classes.link } href={href} onClick={onClick}>{ linkLabel }</Link>
            </div>
        </label>
    );
};

/**
 * Take additional styles and trigger navigate event
 */
export const FormHyperlink = ({label , href, linkLabel, ...props}) => {
    const classes = useTextFieldStyles();

    return (
        <label className={ classes.root }>
            <span className={ classes.label }>
                { label }                
            </span>

            <div className={ classes.formControlInputWithError }>
            <LqxLink.AppLink className={ classes.link } href={href} {...props}>{ linkLabel }</LqxLink.AppLink>

            </div>
        </label>
    );
};

const useTextFieldStyles = makeStyles({
    root : {
        display: 'flex',
        flexDirection: 'row',
        color: '#767D84',
        fontFamily: 'Montserrat',
        fontSize: 12,
        fontWeight: 500,
        marginBottom: 8,

        '&:focus-within' : {
            color: '#1084D3',
            fontWeight: 600,
        }
    },

    required : {
        color: colors.red,
        verticalAlign: 'middle',
        fontSize: 16,
        paddingLeft: 4,
    },

    label: {
        flex: 2,
        alignSelf: 'center',
    },

    disputeLabel: {
        flex: 4,
    },

    disputeValueContainer: {
        flex: 3,
        alignSelf: 'center',
    },

    disputeValue: {
        textAlign: 'left',
    },

    disputeValueChanged: {
        textDecoration: 'line-through'
    },

    formControlInputWithError : {
        flexDirection: 'column',
        flex : 3,
        display: 'inline-flex',
    },

    link:{
        textDecoration: 'none',
        padding:'4px 3px 3px 3px'
    }
})

export const FormLabel = div({
    fontFamily: 'Montserrat',
    flex: 2,
    color: '#767D84',
    alignSelf: 'center',
    fontSize: 12,
    fontWeight: 500,
});

export const FormSelect = withStyles({
    root : {
        display: 'flex',
        flexDirection: 'row',
        color: '#767D84',
        fontFamily: 'Montserrat',
        fontSize: 12,
        fontWeight: 500,

        borderStyle: 'none',

        '&:focus-within' : {
            fontWeight: 600,
        },

        '& .select-input-root' : {
            flexGrow: 3,
        },

        '&.error' : {
            '& .select-root': {
                border: 'none !important',
            },

            '& .select-input-root' : {
                borderRadius: 2,
                border: '1px solid #FF274B !important',
            }
        },
    },

    label : {
        flex: 2,
        alignSelf: 'center',
    },

    selectRoot : {
        borderRadius: 2,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#E3E9ED',

        fontSize: 12,
        fontWeight: 500,
        flex: 3,
        display: 'inline-flex',
    },

    selectInputRoot : {
        flex: 1,
    },

    selectInput : {
        fontSize: 14,
        fontWeight: 500,

        '&:focus' : {
            outline: 'none',
        },

        '&:disabled' : {
            opacity: 0.5,
        }
    },
})(BaseSelect);

export const FormCheckbox = withStyles({
    label: {
        color: '#767D84',
        fontFamily: 'Montserrat',
        fontSize: 12,
        fontWeight: 500,
    },

    checkbox: {
        width: 24,
        height: 24,

        '& div' : {
            width: 17,
            height: 9,
            margin: '5px 3px 3px 3px',
        },
    },
})(BaseCheckbox)

export const RoundCheckbox = withStyles({
    checkbox: {
        height: 17,
        width: 17,
        borderRadius : 10,
        borderColor: '#A0A0A0',

        '& div' : {
            width: 9,
            height: 5,
        },
    },
})(BaseCheckbox)

export const SelectFile = styled(
    ({ className, label, $value, accept }) =>
        <label className={ className }>
            <FormLabel>
                {label}
            </FormLabel>

            <Flex grow />

            <div className="filename" title={ $value.value ? $value.value.name : '' }>
                { $value.value ?
                    $value.value.name
                :
                    'Click to select the file...'
                }
            </div>

            <CloudUploadIcon />
            <input
                style={{ display : 'none' }}
                type="file"
                accept={ accept }
                onChange={ e => $value.set( e.target.files[ 0 ] )}
            />
        </label>
)({
    display: 'flex',
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: '#c4c4c4',
    borderStyle: 'solid',
    padding: 12,
    borderRadius: 4,
    alignItems: 'center',

    marginTop: 4,
    marginBottom: 4,

    '& .filename' : {
        fontFamily: 'Montserrat',
        color: 'black',
        alignSelf: 'center',
        fontSize: 14,
        fontWeight: 500,
        marginRight: '1em',
        marginLeft: '1em',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
    }
})
